<script>
import axios from 'axios'
import SdwanDeviceDetail from './sdwan_device_detail'

const REFRESH_INTERVAL_MS = 60000

export default {
  components: {
    SdwanDeviceDetail
  },
  props: {
    basePath: {
      type: String,
      default: '/monitoring/sdwan'
    }
  },
  data () {
    return {
      // eslint-disable-next-line
      appConfig: process.env.NODE_ENV === 'production' ? require('@src/app.config') : require('@src/app.config.dev'),
      summary: {},
      health: { devices: [], problems: [] },
      profiles: [],
      profileFilter: '',
      sparklines: {},
      sparklinesFailed: false,
      flaps: { '24h': [], '7d': [] },
      flapWindow: '24h',
      devices: [],
      events: [],
      eventsTotal: 0,
      eventsPage: 1,
      eventsPerPage: 25,
      eventsPageOptions: [10, 25, 50, 100],
      eventsDevice: '',
      switchesOnly: true,
      problemsOnly: false,
      siteFilter: '',
      devicesFilter: '',
      devicesPage: 1,
      devicesPerPage: 25,
      devicesTotal: 0,
      isBusy: false,
      eventsBusy: false,
      devicesLoading: true,
      flapsLoading: false,
      healthLoaded: false,
      // Set when the last /health request failed; cleared by the next success.
      healthError: '',
      exporting: '',
      loadError: '',
      lastUpdated: null,
      refreshTimer: null,
      selectedDevice: '',
      deviceFields: [
        { key: 'device', label: 'Device', sortable: true },
        { key: 'ip', label: 'IP', sortable: false },
        { key: 'model', label: 'Model', sortable: true },
        { key: 'sw_version', label: 'Version', sortable: true },
        { key: 'uptime_text', label: 'Uptime', sortable: false },
        { key: 'vif_count', label: 'VIFs', sortable: true },
        { key: 'last_seen', label: 'Last Seen', sortable: true },
        { key: 'status', label: 'Status', sortable: true }
      ]
    }
  },
  computed: {
    baseURL () {
      return this.appConfig.xdrAPI.baseURL || 'http://localhost:5000'
    },
    tierBadges () {
      const collector = this.summary.collector || {}
      return ['fast', 'events', 'slow'].map((tier) => ({
        tier,
        status: (collector[tier] || {}).status || 'unknown'
      }))
    },
    visibleSites () {
      let sites = this.health.devices || []
      if (this.problemsOnly) {
        sites = sites.filter((site) => !['healthy', 'no-data'].includes(site.status))
      }
      const text = this.siteFilter.trim().toLowerCase()
      if (text) {
        sites = sites.filter((site) =>
          site.device.toLowerCase().includes(text) ||
          String(site.display_name || '').toLowerCase().includes(text) ||
          (site.ip || '').includes(text))
      }
      return sites
    },
    // The collector's inventory label is the join key, but operators
    // recognise the hostname the device reports about itself.
    nameIssues () {
      const filas = []
      for (const item of this.devices) {
        if (['mismatch', 'placeholder', 'factory-default'].includes(item.name_status)) {
          filas.push(item)
        }
      }
      return filas.sort((a, b) => a.device.localeCompare(b.device))
    },
    displayNames () {
      const mapa = {}
      for (const site of this.health.devices || []) {
        mapa[site.device] = site.display_name || site.device
      }
      for (const item of this.devices) {
        mapa[item.device] = item.display_name || item.device
      }
      return mapa
    },
    // Problems raised by a device verdict carry the policy name as their
    // scope, and that name matches the rule name in the configured profile,
    // so an active alarm can be pinned to the exact profile that failed.
    profileAlarms () {
      const mapa = {}
      for (const problema of this.health.problems || []) {
        const clave = `${problema.device}|${problema.scope}`
        if (problema.severity === 'critical' || !mapa[clave]) {
          mapa[clave] = problema.severity
        }
      }
      return mapa
    },
    visibleProfiles () {
      const texto = this.profileFilter.trim().toLowerCase()
      const filas = []
      for (const equipo of this.profiles) {
        for (const regla of equipo.rules || []) {
          if (texto &&
              !equipo.device.toLowerCase().includes(texto) &&
              !String(regla.name || '').toLowerCase().includes(texto)) continue
          filas.push({
            device: equipo.device,
            ...regla,
            alarm: this.profileAlarms[`${equipo.device}|${regla.name}`] || null
          })
        }
      }
      // Profiles with an active alarm first: the modal is opened to find them.
      return filas.sort((a, b) =>
        (a.alarm ? 0 : 1) - (b.alarm ? 0 : 1) ||
        a.device.localeCompare(b.device))
    },
    alarmedProfiles () {
      return this.visibleProfiles.filter((fila) => fila.alarm).length
    },
    downTiers () {
      return this.tierBadges.filter((badge) => badge.status === 'down').map((badge) => badge.tier)
    },
    flapSeries () {
      const rows = this.flaps[this.flapWindow] || []
      return [{ name: 'Path switches', data: rows.map((row) => row.count) }]
    },
    flapOptions () {
      const rows = this.flaps[this.flapWindow] || []
      return {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: '55%', borderRadius: 3 } },
        dataLabels: { enabled: true },
        colors: ['#ffbc00'],
        xaxis: { categories: rows.map((row) => row.device) }
      }
    }
  },
  watch: {
    eventsPage () {
      this.loadEvents()
    },
    eventsPerPage () {
      this.eventsPage = 1
      this.loadEvents()
    },
    switchesOnly () {
      this.eventsPage = 1
      this.loadEvents()
    },
    flapWindow () {
      this.loadFlaps()
    }
  },
  mounted () {
    this.loadAll()
    // Background tabs must not keep polling: with several pages open the
    // combined refresh load can starve the API (fatal on the single-threaded
    // dev server). The page catches up on the next tick after regaining focus.
    this.refreshTimer = setInterval(() => {
      if (document.hidden) return
      this.loadAll()
    }, REFRESH_INTERVAL_MS)
  },
  beforeDestroy () {
    if (this.refreshTimer) clearInterval(this.refreshTimer)
  },
  methods: {
    parse (data) {
      return typeof data === 'string' ? JSON.parse(data) : data
    },
    loadAll () {
      // The auto-refresh timer must never stack a new cycle on top of one
      // that is still in flight (slow backend -> ever-growing request queue).
      if (this.isBusy) return
      this.isBusy = true
      this.loadError = ''
      const base = `${this.baseURL}${this.basePath}`
      const fallo = (que) => (error) => {
        this.loadError = `Could not load ${que}: ${error.message}`
      }
      // Each section paints as soon as its own request lands. Waiting for all
      // of them (Promise.all) left the whole page on "Loading..." for as long
      // as the slowest endpoint took.
      const peticiones = [
        axios.get(`${base}/summary`)
          .then((r) => { this.summary = this.parse(r.data) })
          .catch(fallo('the indicators')),
        axios.get(`${base}/health`)
          .then((r) => {
            this.health = this.parse(r.data)
            this.healthLoaded = true
            this.healthError = ''
          })
          .catch((error) => {
            this.healthError = error.message
            fallo('the site status')(error)
          }),
        axios.get(`${base}/devices`)
          .then((r) => {
            const data = this.parse(r.data)
            this.devices = data.items || []
            this.devicesTotal = this.devices.length
          })
          .catch(fallo('the device inventory'))
          .then(() => { this.devicesLoading = false }),
        axios.get(`${base}/profiles`)
          .then((r) => { this.profiles = this.parse(r.data).items || [] })
          .catch(() => { this.profiles = [] }),
        axios.get(`${base}/sparklines`)
          .then((r) => {
            this.sparklines = this.parse(r.data).series || {}
            this.sparklinesFailed = false
          })
          // Keep whatever was already loaded: a failed refresh should not wipe
          // every trend line, and the failure is reported instead of hidden.
          .catch(() => { this.sparklinesFailed = true }),
        this.loadFlaps()
      ]
      Promise.all(peticiones).then(() => {
        this.isBusy = false
        this.lastUpdated = new Date()
      })
      this.loadEvents()
    },
    loadFlaps () {
      // Only the displayed window is requested: scanning 7 days of events
      // costs several times the 24 h window.
      this.flapsLoading = true
      return axios
        .get(`${this.baseURL}${this.basePath}/flaps?window=${this.flapWindow}`)
        .then((r) => {
          this.flaps = { ...this.flaps, ...this.parse(r.data) }
        })
        .catch(() => {})
        .then(() => { this.flapsLoading = false })
    },
    loadEvents () {
      this.eventsBusy = true
      const params = [
        `page=${this.eventsPage}`,
        `page_size=${this.eventsPerPage}`
      ]
      if (this.eventsDevice.trim()) params.push(`device=${encodeURIComponent(this.eventsDevice.trim())}`)
      if (this.switchesOnly) params.push('switches_only=yes')
      axios
        .get(`${this.baseURL}${this.basePath}/events?${params.join('&')}`)
        .then((response) => {
          const data = this.parse(response.data)
          this.events = data.items || []
          this.eventsTotal = data.total_count || 0
        })
        .catch((error) => {
          this.loadError = `Could not load SD-WAN events: ${error.message}`
        })
        .finally(() => {
          this.eventsBusy = false
        })
    },
    applyEventsFilter () {
      this.eventsPage = 1
      this.loadEvents()
    },
    downloadCsv (path, filename) {
      this.exporting = filename
      axios
        .get(`${this.baseURL}${this.basePath}${path}`, { responseType: 'blob' })
        .then((response) => {
          const url = window.URL.createObjectURL(
            new Blob([response.data], { type: 'text/csv' }))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(url)
        })
        .catch((error) => {
          this.loadError = `Could not export ${filename}: ${error.message}`
        })
        .finally(() => {
          this.exporting = ''
        })
    },
    exportEvents () {
      const params = []
      if (this.eventsDevice.trim()) params.push(`device=${encodeURIComponent(this.eventsDevice.trim())}`)
      if (this.switchesOnly) params.push('switches_only=yes')
      const query = params.length ? `?${params.join('&')}` : ''
      this.downloadCsv(`/events/csv${query}`, 'sdwan_path_events.csv')
    },
    tierTip (tier) {
      const tips = {
        fast: 'Fast tier: path statistics collected every 5 minutes.',
        events: 'Events tier: the SD-WAN event log is collected every 15 minutes.',
        slow: 'Slow tier: system info and SD-WAN rules collected hourly.'
      }
      return tips[tier] || ''
    },
    onDetailsShown () {
      // ApexCharts sizes itself against the container width available when it
      // renders. Inside a modal that width is not final until the dialog is
      // shown, and Safari is the strictest about it: without this the chart
      // keeps its stale width and spills outside the modal.
      this.$nextTick(() => window.dispatchEvent(new Event('resize')))
    },
    openNames () {
      this.$refs['modal-names'].show()
    },
    openProfiles () {
      this.$refs['modal-profiles'].show()
    },
    onDevicesFiltered (filteredItems) {
      this.devicesTotal = filteredItems.length
      this.devicesPage = 1
    },
    showDetails (item) {
      this.selectedDevice = typeof item === 'string' ? item : item.device
      this.$refs['modal-details'].show()
    },
    formatDate (value) {
      if (!value) return '—'
      return new Date(value).toLocaleString('en-CO', {
        timeZone: 'America/Bogota',
        hour12: false
      })
    },
    // Discreet marker: the inventory label and the name the device reports
    // about itself do not always agree, and that discrepancy is worth
    // surfacing without turning it into an alarm.
    nameNote (item) {
      const notas = {
        mismatch: `Inventory label: ${item.device} · device reports: ${item.hostname}`,
        placeholder: `The inventory has no name for this device (${item.device}); it identifies itself as ${item.hostname}`,
        'factory-default': `The device has no hostname configured: it answers with its model (${item.hostname})`
      }
      return notas[item.name_status] || ''
    },
    shownName (device) {
      return this.displayNames[device] || device
    },
    metricText (value, unit) {
      return value != null ? `${value} ${unit}` : '—'
    },
    // Inline SVG sparkline (latency trend): apexcharts would be far too heavy
    // for ~300 instances, a polyline is essentially free.
    sparklinePoints (device, vif, ifName) {
      const points = this.sparklines[`${device}|${vif} - ${ifName}`] || []
      const values = points
        .map((point) => (typeof point.l === 'number' ? point.l : null))
        .filter((value) => value !== null)
      if (values.length < 2) return ''
      const max = Math.max(150, ...values)
      const width = 90
      const height = 26
      const step = width / (values.length - 1)
      return values
        .map((value, index) =>
          `${(index * step).toFixed(1)},${(height - 2 - (value / max) * (height - 4)).toFixed(1)}`)
        .join(' ')
    },
    // Tunnel names like tl_0102_007957000760560_0101 differ only in their
    // suffix, so trimming the middle keeps both ends readable; the full name
    // stays in the row's tooltip.
    shortIf (name) {
      const texto = String(name || '')
      if (texto.length <= 20) return texto
      return `${texto.slice(0, 9)}…${texto.slice(-6)}`
    },
    // An interface can be up and still report no quality figures: a member
    // checked with ICMP only proves reachability, it does not measure latency,
    // jitter or loss the way the path monitor does.
    monitorNote (row) {
      const motivo = String(row.stateReason || '').toLowerCase()
      if (motivo.includes('ping')) return 'ping check only'
      if (motivo.includes('ka')) return 'keepalive only'
      return 'no probe data'
    },
    sparkColor (status) {
      // A down path must never be drawn in green: the trend shown is what the
      // link did before it dropped, not evidence that it is healthy.
      if (status === 'down' || status === 'crit') return '#fa5c7c'
      return '#0acf97'
    },
    // Quality is measured per physical interface, so the card lists
    // interfaces (worst first) rather than virtual interfaces.
    sitePaths (site) {
      const order = { crit: 0, warn: 1, ok: 2 }
      const rows = []
      for (const vif of site.vifs || []) {
        for (const path of vif.paths || []) {
          rows.push({
            vif: vif.vif,
            ifName: path.if_name,
            state: path.state,
            stateReason: path.state_reason,
            status: path.status || vif.status,
            metrics: path.metrics || {},
            traffic: path.traffic || {},
            measured: path.measured !== false && path.metrics &&
              typeof path.metrics.latency_ms === 'number'
          })
        }
      }
      const rank = (row) => (row.status in order ? order[row.status] : 9)
      return rows.sort((a, b) =>
        rank(a) - rank(b) ||
        String(a.vif).localeCompare(String(b.vif)) ||
        String(a.ifName).localeCompare(String(b.ifName)))
    },
    // Measured values are shown as information, not as a verdict: the
    // firewall applies its own configured SLA and is the single source of
    // truth for what counts as degraded.
    metricClass (value) {
      return typeof value === 'number' ? '' : 'text-muted'
    },
    slaText (site) {
      const sla = site.sla || {}
      if (typeof sla.latency_ms !== 'number') return ''
      return `Device SLA: latency ≤ ${sla.latency_ms} ms · jitter ≤ ${sla.jitter_ms} ms · loss ≤ ${sla.loss_pct} %`
    },
    siteStatusVariant (status) {
      const variants = {
        healthy: 'success',
        degraded: 'warning',
        critical: 'danger',
        unreachable: 'danger',
        'no-data': 'secondary'
      }
      return variants[status] || 'secondary'
    },
    pathStateVariant (state) {
      if (state === 'up') return 'success'
      if (state === 'down') return 'danger'
      return 'secondary'
    },
    severityVariant (severity) {
      const variants = { critical: 'danger', warning: 'warning' }
      return variants[severity] || 'secondary'
    }
  }
}
</script>

<template>
  <div class="sdwan-page">
    <b-alert
      :show="loadError.length > 0"
      variant="danger"
      dismissible
      @dismissed="loadError = ''"
    >
      {{ loadError }}
    </b-alert>

    <b-alert
      :show="downTiers.length > 0"
      variant="warning"
      class="d-flex align-items-center py-1"
    >
      <feather
        type="alert-triangle"
        class="icon-xs mr-2"
      />
      <span>
        <b>Collector tier{{ downTiers.length > 1 ? 's' : '' }} down:</b> {{ downTiers.join(', ') }} —
        the data below is the last known state; live path metrics and site statuses stop updating
        until the collector runs again.
      </span>
    </b-alert>

    <!-- Toolbar -->
    <div class="d-flex align-items-center flex-wrap mb-2">
      <span
        v-if="lastUpdated"
        class="text-muted font-size-12 mr-3"
      >
        Updated {{ formatDate(lastUpdated) }} (America/Bogota) · auto-refresh 60 s
      </span>
      <b-badge
        v-for="badge in tierBadges"
        :key="badge.tier"
        v-b-tooltip.hover
        :variant="badge.status === 'ok' ? 'success' : 'danger'"
        class="mr-1"
        :title="tierTip(badge.tier)"
      >
        {{ badge.tier }} {{ badge.status }}
      </b-badge>
      <b-button
        size="sm"
        variant="outline-secondary"
        class="ml-auto mr-2"
        title="Quality profiles configured on the devices"
        @click="openProfiles"
      >
        <feather
          type="sliders"
          class="icon-xs mr-1"
        />
        SLA profiles
      </b-button>
      <b-button
        size="sm"
        variant="outline-primary"
        :disabled="isBusy"
        @click="loadAll"
      >
        <feather
          type="refresh-cw"
          class="icon-xs mr-1"
        />
        Refresh
      </b-button>
    </div>

    <!-- KPI cards -->
    <div class="row">
      <div class="col-xl-2 col-md-4 col-6 mb-4">
        <div class="card h-100 mb-0">
          <div class="card-body py-2">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1">
                <h5 class="text-muted font-size-13 mb-1">
                  Devices Healthy
                  <feather
                    v-b-tooltip.hover
                    type="help-circle"
                    class="icon-xs help-tip"
                    title="All monitored paths are up and the firewall reports no SLA failure or quality-driven path switch."
                  />
                </h5>
                <h3 class="mb-0 text-success">
                  {{ summary.devices_healthy || 0 }}<small class="text-muted font-size-13">/{{ summary.devices_total || 0 }}</small>
                </h3>
              </div>
              <div class="avatar-sm rounded-circle bg-success align-self-center">
                <span class="avatar-title bg-transparent">
                  <feather
                    type="check-circle"
                    class="icon-sm"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-2 col-md-4 col-6 mb-4">
        <div class="card h-100 mb-0">
          <div class="card-body py-2">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1">
                <h5 class="text-muted font-size-13 mb-1">
                  Devices Critical
                  <feather
                    v-b-tooltip.hover
                    type="help-circle"
                    class="icon-xs help-tip"
                    title="At least one member path is down, or the firewall found no path meeting its configured SLA."
                  />
                </h5>
                <h3
                  class="mb-0"
                  :class="{ 'text-danger': (summary.devices_critical || 0) > 0 }"
                >
                  {{ summary.devices_critical || 0 }}
                </h3>
              </div>
              <div class="avatar-sm rounded-circle bg-danger align-self-center">
                <span class="avatar-title bg-transparent">
                  <feather
                    type="alert-octagon"
                    class="icon-sm"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-2 col-md-4 col-6 mb-4">
        <div class="card h-100 mb-0">
          <div class="card-body py-2">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1">
                <h5 class="text-muted font-size-13 mb-1">
                  Unreachable
                  <feather
                    v-b-tooltip.hover
                    type="help-circle"
                    class="icon-xs help-tip"
                    title="The collector could not reach the device in the last 30 minutes (connect, timeout or auth error)."
                  />
                </h5>
                <h3
                  class="mb-0"
                  :class="{ 'text-danger': (summary.devices_unreachable || 0) > 0 }"
                >
                  {{ summary.devices_unreachable || 0 }}
                </h3>
              </div>
              <div class="avatar-sm rounded-circle bg-secondary align-self-center">
                <span class="avatar-title bg-transparent">
                  <feather
                    type="wifi-off"
                    class="icon-sm"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-2 col-md-4 col-6 mb-4">
        <div class="card h-100 mb-0">
          <div class="card-body py-2">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1">
                <h5 class="text-muted font-size-13 mb-1">
                  Paths Down
                  <feather
                    v-b-tooltip.hover
                    type="help-circle"
                    class="icon-xs help-tip"
                    title="Member paths reporting state 'down', out of all monitored member paths."
                  />
                </h5>
                <h3
                  class="mb-0"
                  :class="{ 'text-danger': (summary.paths_down || 0) > 0 }"
                >
                  {{ summary.paths_down || 0 }}<small class="text-muted font-size-13">/{{ summary.paths_total || 0 }}</small>
                </h3>
              </div>
              <div class="avatar-sm rounded-circle bg-danger align-self-center">
                <span class="avatar-title bg-transparent">
                  <feather
                    type="trending-down"
                    class="icon-sm"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-2 col-md-4 col-6 mb-4">
        <div class="card h-100 mb-0">
          <div class="card-body py-2">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1">
                <h5 class="text-muted font-size-13 mb-1">
                  SLA Failed (24 h)
                  <feather
                    v-b-tooltip.hover
                    type="help-circle"
                    class="icon-xs help-tip"
                    title="Devices where the firewall itself found no path meeting its configured SD-WAN quality profile in the last 24 hours."
                  />
                </h5>
                <h3
                  class="mb-0"
                  :class="{ 'text-danger': (summary.sla_failed_devices || 0) > 0 }"
                >
                  {{ summary.sla_failed_devices || 0 }}
                </h3>
              </div>
              <div class="avatar-sm rounded-circle bg-warning align-self-center">
                <span class="avatar-title bg-transparent">
                  <feather
                    type="alert-triangle"
                    class="icon-sm"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-2 col-md-4 col-6 mb-4">
        <div class="card h-100 mb-0">
          <div class="card-body py-2">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1">
                <h5 class="text-muted font-size-13 mb-1">
                  Path Switches (24 h)
                  <feather
                    v-b-tooltip.hover
                    type="help-circle"
                    class="icon-xs help-tip"
                    title="Times a firewall actually moved traffic to a different path. Excludes initial selections and re-evaluations that kept the same path."
                  />
                </h5>
                <h3 class="mb-0">
                  {{ summary.path_switches_24h || 0 }}
                </h3>
              </div>
              <div class="avatar-sm rounded-circle bg-primary align-self-center">
                <span class="avatar-title bg-transparent">
                  <feather
                    type="shuffle"
                    class="icon-sm"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Active problems -->
    <div class="row">
      <div class="col-12 mb-4">
        <div class="card mb-0">
          <div class="card-body py-2">
            <div class="d-flex align-items-center mb-2">
              <h5 class="font-size-14 mb-0">
                <feather
                  type="alert-circle"
                  class="icon-xs mr-1 text-danger"
                />
                Active problems
                <b-badge
                  variant="light"
                  class="ml-1"
                >
                  {{ (health.problems || []).length }}
                </b-badge>
                <feather
                  v-b-tooltip.hover
                  type="help-circle"
                  class="icon-xs help-tip"
                  title="Conditions the firewalls themselves report, judged against the SD-WAN quality profiles configured on each Palo Alto device (see SLA profiles): a path down, a policy whose SLA no path could meet, traffic moved to another path on quality, or a device the collector cannot reach. Red (critical) = down, SLA not met, or unreachable; amber (warning) = a quality-driven path switch."
                />
              </h5>
              <b-button
                size="sm"
                variant="outline-secondary"
                class="ml-auto"
                title="Export active problems as CSV"
                :disabled="exporting.length > 0"
                @click="downloadCsv('/problems/csv', 'sdwan_active_problems.csv')"
              >
                <feather
                  type="download"
                  class="icon-xs mr-1"
                />
                CSV
              </b-button>
            </div>
            <div
              v-if="(health.problems || []).length"
              style="max-height: min(240px, 30vh); overflow: auto;"
            >
              <div
                v-for="(problem, index) in health.problems"
                :key="`problem-${index}`"
                class="row align-items-center border-bottom py-1 mx-0 font-size-13 flex-nowrap sdwan-fixed-row"
              >
                <div
                  class="px-0 text-center"
                  style="width: 70px; flex: none;"
                >
                  <b-badge :variant="severityVariant(problem.severity)">
                    {{ problem.severity }}
                  </b-badge>
                </div>
                <div
                  class="px-2 text-truncate font-weight-bold"
                  style="width: 220px; flex: none;"
                  :title="problem.device"
                >
                  <a
                    href="javascript:void(0)"
                    @click="showDetails(problem.device)"
                  >{{ problem.device }}</a>
                </div>
                <div
                  class="px-2 text-muted text-truncate"
                  style="width: 30%; flex: none;"
                  :title="problem.scope"
                >
                  {{ problem.scope }}
                </div>
                <div
                  class="col px-2 text-truncate"
                  :title="problem.condition"
                >
                  {{ problem.condition }}
                </div>
                <div
                  class="px-0 text-muted text-nowrap text-right font-size-11"
                  style="width: 135px; flex: none;"
                >
                  {{ formatDate(problem.ts) }}
                </div>
              </div>
            </div>
            <div
              v-else-if="healthError"
              class="text-danger py-3 text-center font-size-13"
            >
              Could not load the problem list: {{ healthError }}
            </div>
            <div
              v-else-if="!healthLoaded"
              class="text-muted py-3 text-center"
            >
              <b-spinner small />&nbsp;&nbsp;Loading problems...
            </div>
            <div
              v-else
              class="text-muted py-3 text-center"
            >
              No active problems.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Site grid -->
    <div class="d-flex align-items-center mb-2">
      <h5 class="font-size-14 mb-0">
        <feather
          type="map-pin"
          class="icon-xs mr-1 text-muted"
        />
        Sites
        <b-badge
          variant="light"
          class="ml-1"
        >
          {{ visibleSites.length }}
        </b-badge>
        <feather
          v-b-tooltip.hover
          type="help-circle"
          class="icon-xs help-tip"
          title="One card per firewall. A VIF (e.g. sdwan.901) is an SD-WAN virtual interface bundling member paths (links/tunnels, shown as dots: green up, red down). Lat/Jit/Loss are the current worst-case values across the vif's sensitivity buckets; Trend is latency over the last 2 hours. Worst 4 VIFs shown - open the device for the full list."
        />
      </h5>
      <b-form-checkbox
        v-model="problemsOnly"
        switch
        class="ml-3"
      >
        problems only
      </b-form-checkbox>
      <b-badge
        v-if="sparklinesFailed"
        variant="light"
        class="ml-2 text-muted"
        title="The trend request did not complete; figures are still current."
      >
        trends unavailable
      </b-badge>
      <b-form-input
        v-model="siteFilter"
        size="sm"
        placeholder="Search device or IP..."
        class="ml-auto"
        style="max-width: 240px;"
      />
    </div>
    <div class="row">
      <div
        v-for="site in visibleSites"
        :key="site.device"
        class="col-xl-4 col-lg-6 mb-4 sdwan-site-col"
      >
        <div class="card h-100 mb-0">
          <div class="card-body py-2">
            <div class="d-flex align-items-center">
              <feather
                type="hard-drive"
                class="icon-xs mr-1 text-muted flex-shrink-0"
              />
              <a
                href="javascript:void(0)"
                class="font-weight-bold text-truncate"
                :title="site.display_name !== site.device ? `Inventory label: ${site.device}` : site.device"
                @click="showDetails(site.device)"
              >{{ site.display_name || site.device }}</a>
              <feather
                v-if="nameNote(site)"
                v-b-tooltip.hover
                type="alert-circle"
                class="icon-xs help-tip ml-1"
                :title="nameNote(site)"
              />
              <b-badge
                :variant="siteStatusVariant(site.status)"
                class="ml-auto"
              >
                {{ site.status }}
              </b-badge>
            </div>
            <div class="text-muted font-size-11 mb-1">
              {{ site.ip || '—' }}
              <template v-if="site.model">
                · {{ site.model }} · {{ site.sw_version }}
              </template>
              <template v-if="typeof site.tunnels_total === 'number'">
                · tunnels {{ site.tunnels_up }}/{{ site.tunnels_total }}
              </template>
            </div>

            <div
              v-if="site.status === 'unreachable' && site.error"
              class="text-danger font-size-12"
            >
              <feather
                type="wifi-off"
                class="icon-xs mr-1"
              />
              {{ site.error.error_type }}: {{ site.error.meaning }}
            </div>
            <div
              v-else-if="!site.vifs.length"
              class="text-muted font-size-12"
            >
              <feather
                type="pause-circle"
                class="icon-xs mr-1"
              />
              No active vifs (HA-passive member or no current data).
            </div>

            <table
              v-if="site.vifs.length"
              class="table table-borderless sdwan-paths mb-0 mt-1"
            >
              <thead>
                <tr class="text-muted text-uppercase">
                  <th class="pl-0">
                    Interface
                  </th>
                  <th class="text-right">
                    Lat
                  </th>
                  <th class="text-right">
                    Jit
                  </th>
                  <th class="text-right">
                    Loss
                  </th>
                  <th class="text-right pr-0">
                    Trend
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in sitePaths(site).slice(0, 5)"
                  :key="`${site.device}-${row.vif}-${row.ifName}`"
                  class="sdwan-vif-row"
                >
                  <td
                    class="pl-0"
                    :title="`${row.vif} · ${row.ifName} (${row.stateReason || row.state || 'no state'})`"
                  >
                    <div class="sdwan-if">
                      <span :class="`text-${pathStateVariant(row.state)}`">●</span>
                      <span class="sdwan-if-name">{{ shortIf(row.ifName) }}</span>
                    </div>
                    <div
                      v-if="row.state === 'down'"
                      class="sdwan-members text-danger"
                    >
                      down · no measurement
                    </div>
                    <div
                      v-else-if="!row.measured"
                      class="sdwan-members"
                      :title="row.stateReason"
                    >
                      {{ row.vif }} · {{ monitorNote(row) }}
                    </div>
                    <div
                      v-else
                      class="sdwan-members"
                    >
                      {{ row.vif }}
                    </div>
                  </td>
                  <td
                    class="text-right text-nowrap"
                    :class="metricClass(row.metrics.latency_ms)"
                  >
                    {{ metricText(row.metrics.latency_ms, 'ms') }}
                  </td>
                  <td
                    class="text-right text-nowrap"
                    :class="metricClass(row.metrics.jitter_ms)"
                  >
                    {{ metricText(row.metrics.jitter_ms, 'ms') }}
                  </td>
                  <td
                    class="text-right text-nowrap"
                    :class="metricClass(row.metrics.loss_pct)"
                  >
                    {{ metricText(row.metrics.loss_pct, '%') }}
                  </td>
                  <td class="text-right pr-0">
                    <svg
                      v-if="sparklinePoints(site.device, row.vif, row.ifName)"
                      class="sdwan-spark"
                      viewBox="0 0 90 26"
                      preserveAspectRatio="none"
                    >
                      <polyline
                        :points="sparklinePoints(site.device, row.vif, row.ifName)"
                        fill="none"
                        :stroke="sparkColor(row.status)"
                        stroke-width="1.5"
                        stroke-linejoin="round"
                      />
                    </svg>
                    <span
                      v-else
                      class="text-muted"
                    >—</span>
                  </td>
                </tr>
                <tr v-if="sitePaths(site).length > 5">
                  <td
                    colspan="5"
                    class="pl-0 pt-1"
                  >
                    <a
                      href="javascript:void(0)"
                      class="font-size-11"
                      @click="showDetails(site.device)"
                    >
                      + {{ sitePaths(site).length - 5 }} more interfaces — view details
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div
        v-if="!visibleSites.length && healthLoaded"
        class="col-12 text-muted text-center py-4"
      >
        No sites match the current filters.
      </div>
    </div>

    <!-- Flap ranking -->
    <div class="row">
      <div class="col-12 mb-4">
        <div class="card h-100 mb-0">
          <div class="card-body py-2">
            <div class="d-flex align-items-center mb-1">
              <h5 class="font-size-14 mb-0">
                <feather
                  type="shuffle"
                  class="icon-xs mr-1 text-muted"
                />
                Top path switching devices
                <feather
                  v-b-tooltip.hover
                  type="help-circle"
                  class="icon-xs help-tip"
                  title="Ranking by real path switches: the firewall moved traffic to a different path. Frequent switching usually means an unstable circuit (flapping) and is the shortlist for ISP escalation."
                />
              </h5>
              <b-form-select
                v-model="flapWindow"
                size="sm"
                :options="[{ value: '24h', text: 'Last 24 hours' }, { value: '7d', text: 'Last 7 days' }]"
                class="ml-auto"
                style="max-width: 150px;"
              />
              <b-button
                size="sm"
                variant="outline-secondary"
                class="ml-2"
                title="Export ranking (both windows) as CSV"
                :disabled="exporting.length > 0"
                @click="downloadCsv('/flaps/csv', 'sdwan_path_switch_ranking.csv')"
              >
                <feather
                  type="download"
                  class="icon-xs mr-1"
                />
                CSV
              </b-button>
            </div>
            <apexchart
              v-if="(flaps[flapWindow] || []).length"
              type="bar"
              :height="Math.max(200, (flaps[flapWindow] || []).length * 28)"
              :series="flapSeries"
              :options="flapOptions"
            />
            <div
              v-else-if="flapsLoading"
              class="text-muted py-4 text-center"
            >
              <b-spinner small />&nbsp;&nbsp;Loading ranking...
            </div>
            <div
              v-else
              class="text-muted py-4 text-center"
            >
              No path switches in this window.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Devices -->
    <div class="row">
      <div class="col-12 mb-4">
        <div class="card h-100 mb-0">
          <div class="card-body py-2">
            <div class="d-flex align-items-center mb-2">
              <h5 class="font-size-14 mb-0">
                <feather
                  type="server"
                  class="icon-xs mr-1 text-muted"
                />
                Devices
                <b-badge
                  variant="light"
                  class="ml-1"
                >
                  {{ devicesTotal }}
                </b-badge>
                <feather
                  v-b-tooltip.hover
                  type="help-circle"
                  class="icon-xs help-tip"
                  title="Inventory from the hourly system-info report, joined with uptime from the production firewall monitor. VIFs = SD-WAN virtual interfaces currently reporting; no-data usually means an HA-passive member."
                />
                <a
                  v-if="nameIssues.length"
                  href="javascript:void(0)"
                  class="text-muted font-size-11 font-weight-normal ml-2"
                  @click="openNames"
                >
                  {{ nameIssues.length }} naming inconsistencies
                </a>
              </h5>
              <b-form-input
                v-model="devicesFilter"
                size="sm"
                placeholder="Search..."
                class="ml-auto"
                style="max-width: 200px;"
              />
              <b-button
                size="sm"
                variant="outline-secondary"
                class="ml-2"
                title="Export device inventory as CSV"
                :disabled="exporting.length > 0"
                @click="downloadCsv('/devices/csv', 'sdwan_devices.csv')"
              >
                <feather
                  type="download"
                  class="icon-xs mr-1"
                />
                CSV
              </b-button>
            </div>
            <div class="table-responsive mb-0">
              <b-table
                :items="devices"
                :fields="deviceFields"
                :busy="devicesLoading"
                :per-page="devicesPerPage"
                :current-page="devicesPage"
                :filter="devicesFilter"
                responsive="sm"
                show-empty
                hover
                small
                empty-text="No SD-WAN devices reported."
                @filtered="onDevicesFiltered"
                @row-clicked="showDetails"
              >
                <template v-slot:table-busy>
                  <div class="text-center">
                    <br><br>
                    <b-spinner class="align-middle" />
                    <strong>&nbsp;&nbsp;Loading...</strong>
                  </div>
                </template>
                <template v-slot:cell(device)="data">
                  <a
                    href="javascript:void(0)"
                    :title="data.item.device"
                    @click.stop="showDetails(data.item)"
                  >{{ data.item.display_name || data.item.device }}</a>
                  <feather
                    v-if="nameNote(data.item)"
                    v-b-tooltip.hover
                    type="alert-circle"
                    class="icon-xs help-tip ml-1"
                    :title="nameNote(data.item)"
                  />
                </template>
                <template v-slot:cell(last_seen)="data">
                  {{ formatDate(data.item.last_seen) }}
                </template>
                <template v-slot:cell(status)="data">
                  <b-badge :variant="siteStatusVariant(data.item.status)">
                    {{ data.item.status }}
                  </b-badge>
                </template>
              </b-table>
            </div>
            <div class="dataTables_paginate paging_simple_numbers float-right">
              <ul class="pagination pagination-rounded mb-0">
                <b-pagination
                  v-model="devicesPage"
                  :total-rows="devicesTotal"
                  :per-page="devicesPerPage"
                  size="sm"
                />
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Events -->
    <div class="row">
      <div class="col-12 mb-4">
        <div class="card mb-0">
          <div class="card-body py-2">
            <div class="d-flex align-items-center flex-wrap mb-2">
              <h5 class="font-size-14 mb-0">
                <feather
                  type="activity"
                  class="icon-xs mr-1 text-muted"
                />
                Path events
                <b-badge
                  variant="light"
                  class="ml-1"
                >
                  {{ eventsTotal }}
                </b-badge>
                <feather
                  v-b-tooltip.hover
                  type="help-circle"
                  class="icon-xs help-tip"
                  title="Path selection decisions logged by the firewalls (last 24 h). 'Collected' is when the event reached the database; 'Device time' is the firewall's local clock. 'Real switches only' hides initial selections and re-evaluations that kept the same path."
                />
              </h5>
              <b-form-checkbox
                v-model="switchesOnly"
                switch
                class="ml-3"
              >
                real switches only
              </b-form-checkbox>
              <b-form-input
                v-model="eventsDevice"
                size="sm"
                placeholder="Filter by device..."
                class="ml-auto mr-2"
                style="max-width: 220px;"
                @keyup.enter="applyEventsFilter"
              />
              <b-button
                size="sm"
                variant="outline-secondary"
                @click="applyEventsFilter"
              >
                Apply
              </b-button>
              <b-button
                size="sm"
                variant="outline-secondary"
                class="ml-2"
                title="Export the filtered events (up to 5000 rows, last 24 h) as CSV"
                :disabled="exporting.length > 0"
                @click="exportEvents"
              >
                <feather
                  type="download"
                  class="icon-xs mr-1"
                />
                CSV
              </b-button>
            </div>
            <div class="table-responsive mb-0">
              <b-table
                :items="events"
                :fields="[
                  { key: 'ts', label: 'Collected' },
                  { key: 'tick', label: 'Device Time' },
                  { key: 'device', label: 'Device' },
                  { key: 'from', label: 'From' },
                  { key: 'to', label: 'To' },
                  { key: 'reason', label: 'Reason' },
                  { key: 'policy', label: 'Policy' },
                  { key: 'sessions', label: 'Sessions' },
                ]"
                :busy="eventsBusy"
                :per-page="0"
                responsive="sm"
                show-empty
                small
                empty-text="No SD-WAN path events match the current filters (last 24 h)."
              >
                <template v-slot:table-busy>
                  <div class="text-center">
                    <br><br>
                    <b-spinner class="align-middle" />
                    <strong>&nbsp;&nbsp;Loading...</strong>
                  </div>
                </template>
                <template v-slot:head(reason)>
                  Reason
                  <feather
                    v-b-tooltip.hover
                    type="help-circle"
                    class="icon-xs help-tip"
                    title="initial = first selection after boot/config · *-keep = path re-evaluated and kept · best-available / top-down = traffic moved to another path, per the policy's distribution method"
                  />
                </template>
                <template v-slot:cell(ts)="data">
                  {{ formatDate(data.item.ts) }}
                </template>
                <template v-slot:cell(device)="data">
                  <a
                    href="javascript:void(0)"
                    :title="data.item.device"
                    @click="showDetails(data.item.device)"
                  >{{ shownName(data.item.device) }}</a>
                </template>
                <template v-slot:cell(reason)="data">
                  <b-badge :variant="['best-available', 'top-down'].includes(data.item.reason) ? 'warning' : 'light'">
                    {{ data.item.reason }}
                  </b-badge>
                </template>
              </b-table>
            </div>
            <div class="row">
              <div class="col-sm-12 col-md-6">
                <div class="dataTables_length">
                  <label class="d-inline-flex align-items-center">
                    Show&nbsp;
                    <b-form-select
                      v-model="eventsPerPage"
                      size="sm"
                      :options="eventsPageOptions"
                    />&nbsp;entries
                  </label>
                </div>
              </div>
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination
                      v-model="eventsPage"
                      :total-rows="eventsTotal"
                      :per-page="eventsPerPage"
                    />
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal
      id="sdwan-device-details"
      ref="modal-details"
      size="xl"
      :title="`SD-WAN device: ${selectedDevice}`"
      :centered="true"
      :no-fade="true"
      ok-only
      @shown="onDetailsShown"
    >
      <SdwanDeviceDetail
        :device="selectedDevice"
        :base-url="baseURL"
        :base-path="basePath"
      />
    </b-modal>

    <b-modal
      id="sdwan-names"
      ref="modal-names"
      size="lg"
      title="Naming inconsistencies"
      :centered="true"
      :no-fade="true"
      ok-only
      ok-title="Close"
    >
      <p class="text-muted font-size-12 mb-2">
        Each device is identified twice: by the label the collector's inventory
        carries, and by the hostname the device reports about itself. The list
        below is where the two disagree. It affects naming only — monitoring
        works from the inventory label, which is the key that joins the data.
      </p>
      <div
        class="table-responsive"
        style="max-height: min(420px, 45vh); overflow-y: auto;"
      >
        <table class="table table-sm font-size-12 mb-0">
          <thead>
            <tr class="text-muted">
              <th>Inventory label</th>
              <th>Device reports</th>
              <th>IP</th>
              <th>Finding</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in nameIssues"
              :key="`name-${item.device}`"
            >
              <td class="text-nowrap">
                {{ item.device }}
              </td>
              <td class="text-nowrap font-weight-bold">
                {{ item.hostname || '—' }}
              </td>
              <td class="text-muted text-nowrap">
                {{ item.ip || '—' }}
              </td>
              <td>
                <b-badge :variant="item.name_status === 'factory-default' ? 'warning' : 'light'">
                  {{ item.name_status === 'mismatch' ? 'names differ'
                    : item.name_status === 'placeholder' ? 'inventory has no name'
                      : 'device not named' }}
                </b-badge>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-modal>

    <b-modal
      id="sdwan-profiles"
      ref="modal-profiles"
      size="lg"
      title="Quality profiles configured on the devices"
      :centered="true"
      :no-fade="true"
      ok-only
      ok-title="Close"
    >
      <p class="text-muted font-size-12 mb-2">
        These are the thresholds each firewall actually enforces: when they are
        breached, the device moves traffic. The dashboard defines no thresholds
        of its own — it reports the device's verdicts, so the operation works
        from a single criterion.
      </p>
      <b-alert
        :show="alarmedProfiles > 0"
        variant="warning"
        class="py-1 font-size-12"
      >
        <feather
          type="alert-triangle"
          class="icon-xs mr-1"
        />
        {{ alarmedProfiles }} profile(s) with an active alarm, listed first.
      </b-alert>
      <b-form-input
        v-model="profileFilter"
        size="sm"
        placeholder="Filter by device or policy..."
        class="mb-2"
      />
      <div
        class="table-responsive"
        style="max-height: min(420px, 45vh); overflow-y: auto;"
      >
        <table class="table table-sm font-size-12 mb-0">
          <thead>
            <tr class="text-muted">
              <th>Device</th>
              <th>Policy</th>
              <th>Distribution</th>
              <th class="text-right">
                Latency
              </th>
              <th class="text-right">
                Jitter
              </th>
              <th class="text-right">
                Loss
              </th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(fila, index) in visibleProfiles"
              :key="`perfil-${index}`"
              :class="fila.alarm ? `sdwan-profile-${fila.alarm}` : ''"
            >
              <td
                class="text-nowrap"
                :title="fila.device"
              >
                {{ shownName(fila.device) }}
              </td>
              <td>{{ fila.name || '—' }}</td>
              <td class="text-muted">
                {{ fila.dist || '—' }}
              </td>
              <td class="text-right text-nowrap">
                {{ typeof fila.latency_ms === 'number' ? `≤ ${fila.latency_ms} ms` : '—' }}
              </td>
              <td class="text-right text-nowrap">
                {{ typeof fila.jitter_ms === 'number' ? `≤ ${fila.jitter_ms} ms` : '—' }}
              </td>
              <td class="text-right text-nowrap">
                {{ typeof fila.loss_pct === 'number' ? `≤ ${fila.loss_pct} %` : '—' }}
              </td>
              <td class="text-nowrap">
                <b-badge
                  v-if="fila.alarm"
                  :variant="fila.alarm === 'critical' ? 'danger' : 'warning'"
                >
                  {{ fila.alarm === 'critical' ? 'SLA breached' : 'path switched' }}
                </b-badge>
                <span
                  v-else
                  class="text-muted"
                >—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        v-if="!visibleProfiles.length"
        class="text-muted py-3 text-center"
      >
        No profiles match the current filter.
      </div>
    </b-modal>
  </div>
</template>

<style scoped>
.sdwan-paths {
  font-size: 0.79rem;
  table-layout: fixed;
}
.sdwan-paths th,
.sdwan-paths td {
  padding: 2px 4px;
  vertical-align: middle;
}
.sdwan-paths th {
  font-size: 0.72rem;
  letter-spacing: 0.4px;
  font-weight: 600;
}
.sdwan-paths th:nth-child(1) {
  width: auto;
}
/* Column widths in ch, not px: they track the font size, so a reader using
   macOS "Larger Text" gets wider columns instead of overlapping values. */
.sdwan-paths th:nth-child(2),
.sdwan-paths th:nth-child(3) {
  width: 7ch;
}
.sdwan-paths th:nth-child(4) {
  width: 6ch;
}
.sdwan-paths th:nth-child(5) {
  width: 9ch;
}
/* With table-layout: fixed a long value would overlap its neighbour instead
   of widening the column, so every cell clips its own content. */
.sdwan-paths td {
  overflow: hidden;
  text-overflow: ellipsis;
}
.sdwan-spark {
  width: 100%;
  max-width: 90px;
  height: 1.9em;
}
.sdwan-vif-row {
  border-top: 1px solid #eef2f7;
}
/* Bootstrap's largest breakpoint is 1200px, but three site cards only have
   room to breathe from about 1464px up; below that they drop to two. */
@media (min-width: 1200px) and (max-width: 1463.98px) {
  .sdwan-site-col {
    flex: 0 0 50%;
    max-width: 50%;
  }
}
.sdwan-paths th {
  white-space: nowrap;
}
/* The columns are fixed so they line up across rows; on a narrow window the
   container scrolls instead of letting the cells overlap. */
.sdwan-fixed-row {
  min-width: 680px;
}
/* On a small laptop - or any window under ~1100px - the fixed columns stop
   being an alignment aid and start forcing a horizontal scroll, so the row
   reflows into stacked lines instead. Inline widths need !important here. */
@media (max-width: 1100px) {
  .sdwan-fixed-row {
    min-width: 0;
    flex-wrap: wrap;
  }
  .sdwan-fixed-row > div {
    width: auto !important;
    flex: 0 0 auto !important;
    max-width: 100%;
    text-align: left !important;
  }
}
.sdwan-if {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}
.sdwan-if-name {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sdwan-members {
  font-size: 0.72rem;
  color: #98a6ad;
  line-height: 1.6;
}
.sdwan-profile-critical {
  background-color: #fdf0f2;
  box-shadow: inset 3px 0 0 #fa5c7c;
}
.sdwan-profile-warning {
  background-color: #fff8e6;
  box-shadow: inset 3px 0 0 #ffbc00;
}
.help-tip {
  cursor: help;
  color: #ced4da;
}
.help-tip:hover {
  color: #98a6ad;
}
</style>

<style>
/* Unscoped on purpose: the svg inside vue-feather's root element does not
   carry the scoped attribute (scoped rules cannot reach it). */
/* Inline-flex wrapper + block svg keeps inline icons vertically centered on
   the text with no negative margins, so nothing can overflow or clip.
   vertical-align applies to the wrapper relative to the text baseline;
   inside flex containers it is simply ignored, so those stay untouched. */
.sdwan-page .icon-xs {
  display: inline-flex;
  align-items: center;
  vertical-align: -0.15em;
  line-height: 1;
}
.sdwan-page .icon-xs svg {
  display: block;
  margin: 0;
}
/* Help marks are annotations, not content: smaller and lighter than the
   decorative icons. Overrides the theme's .icon-xs > svg 16px !important. */
.sdwan-page .help-tip svg {
  width: 12px !important;
  height: 12px !important;
}
</style>
